import { SyntheticEvent, useCallback, useState, type MouseEvent, type ReactNode } from "react";
import { Body, Link, SectionHeader } from "@sprout/typography";
import CompanyLogo from "@components/company_logo";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "@inertiajs/react";
import { Modal, useModal } from "@inertiaui/modal-react";
import SearchIcon from "@/sprout/icons/search";
import { TextInput } from "@sprout/text_input.tsx";
import "./subscription.scss";
import useTranslation from "@hooks/use_translation";
import { useShowFlash } from "@sprout/flash.tsx";
import Container from "@components/ui/container.tsx";
import BackButton from "@components/back_button.tsx";
import KeywordTags from "@components/keyword_tags.tsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/ui/dialog.tsx";
import { Trans } from "react-i18next";
import ErrorIcon from "@sprout/icons/error.tsx";
import { EnableAlertsModal } from "@components/job_alerts/enable_alerts_modal.tsx";
import HelpPopover from "@sprout/help_popover.tsx";
import { MultiSelect, type Option } from "@sprout/select";
import axios, { isAxiosError } from "axios";
import { DangerButton, DestructiveButton, PrimaryButton, SecondaryButton } from "@sprout/buttons";

type Method = "PUT" | "POST";

type Props = {
  departments: Option[];
  selected: Option[];
  pageTitle: string;
  subscriptionPath: string;
  subscriptionsPath: string;
  dashboardPath: string;
  hasSubscriptions: boolean;
  subscriptionId: number;
  method: Method;
  logoUrl?: string;
  companyName: string;
  jobBoardId: number;
  allowDepartmentAlerts: boolean;
  keywords: string[];
  keywordSeeded: boolean;
  departmentSeeded: boolean;
  jobAlertsPaused: boolean;
  emailPreferencesPath?: string;
};

const ConfirmationModal = ({
  onConfirm,
  children,
}: {
  onConfirm: () => void;
  children: ReactNode;
}) => {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="leading-[1.5rem]">
            <div className="flex gap-4">
              <ErrorIcon color="error-red" />
              <Trans i18nKey="jobAlerts:subscription:deleteModal:title" />
            </div>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Body>
            <Trans i18nKey="jobAlerts:subscription:deleteModal:description" />
          </Body>
        </DialogDescription>
        <DialogFooter>
          <DialogClose>
            <SecondaryButton>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:cancel" />
            </SecondaryButton>
          </DialogClose>
          <DialogClose>
            <DestructiveButton onClick={onConfirm}>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:confirm" />
            </DestructiveButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteConfirmation = ({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return (
    <>
      <DialogHeader>
        <div className="flex gap-4">
          <ErrorIcon color="error-red" />
          <SectionHeader as="h2">
            <Trans i18nKey="jobAlerts:subscription:deleteModal:title" />
          </SectionHeader>
        </div>
      </DialogHeader>
      <Body>
        <Trans i18nKey="jobAlerts:subscription:deleteModal:description" />
      </Body>
      <DialogFooter>
        <SecondaryButton onClick={onCancel}>
          <Trans i18nKey="jobAlerts:subscription:deleteModal:cancel" />
        </SecondaryButton>
        <DestructiveButton onClick={onConfirm}>
          <Trans i18nKey="jobAlerts:subscription:deleteModal:confirm" />
        </DestructiveButton>
      </DialogFooter>
    </>
  );
};

const Subscription = ({
  departments,
  method,
  selected,
  pageTitle,
  subscriptionPath,
  subscriptionsPath,
  dashboardPath,
  hasSubscriptions,
  subscriptionId,
  logoUrl,
  companyName,
  jobBoardId,
  keywords,
  keywordSeeded,
  departmentSeeded,
  allowDepartmentAlerts,
  jobAlertsPaused,
  emailPreferencesPath,
}: Props) => {
  const { t } = useTranslation("jobAlerts");
  const [isDirty, setIsDirty] = useState(keywordSeeded || departmentSeeded);
  const [selectedDepartments, setSelectedDepartments] = useState<Option[]>(selected);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formKeywords, setFormKeywords] = useState<string[]>(keywords || []);
  const [newKeyword, setNewKeyword] = useState<string>("");
  const [keywordError, setKeywordError] = useState<string | null>(null);
  const [departmentError, setDepartmentError] = useState<string | null>(null);
  const [showPausedModal, setShowPausedModal] = useState(method === "POST" && jobAlertsPaused);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const queryClient = useQueryClient();
  const modal = useModal();
  const showFlash = useShowFlash();

  const returnPath = hasSubscriptions ? subscriptionsPath : dashboardPath;
  const closeForm = (fallbackPath: string) => (modal ? modal.close() : router.visit(fallbackPath));

  const handleCancel = modal
    ? (event: MouseEvent) => {
        event.preventDefault();
        closeForm(returnPath);
      }
    : undefined;

  const isKeywordEmpty = newKeyword.trim().length === 0;

  const submitSubscriptionSettings = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    if (selectedDepartments.length > 8) {
      setDepartmentError(t("subscription.department.too_many"));
      return;
    }
    if (formKeywords.length > 8) {
      setKeywordError(t("subscription.keywords.too_many"));
      return;
    }
    setIsSubmitting(true);

    try {
      const payload = {
        id: subscriptionId,
        job_board_id: jobBoardId,
        departments: selectedDepartments.map((department: Option) => department.value),
        keywords: formKeywords,
      };

      await axios({
        method,
        url: subscriptionPath,
        data: payload,
      });

      setIsDirty(false);
      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      showFlash(t(method === "POST" ? "subscription.created" : "subscription.updated"), "success");
      closeForm(method === "POST" ? "/dashboard" : "/subscriptions");
    } catch (e) {
      const errorMessage = (isAxiosError(e) && e.response?.data?.message) ?? t("defaultError");
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, selectedDepartments, queryClient, router, formKeywords, showFlash]);

  const onSelect = useCallback(
    (values: Option[]) => {
      if (values.length > 8) {
        setDepartmentError(t("subscription.department.too_many"));
        return;
      }

      setDepartmentError(null);
      setSelectedDepartments(values);
      setIsDirty(true);
    },
    [selectedDepartments],
  );

  const handleDelete = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    try {
      await axios.delete(subscriptionPath);
      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      closeForm("/subscriptions");
    } catch (e) {
      console.error(e);
      setConfirmingDelete(false);
      setError(t("defaultError"));
      setKeywordError(t("defaultError"));
    }
  }, [subscriptionPath, queryClient, t]);

  const addKeyword = (e: SyntheticEvent) => {
    e.preventDefault();

    // Prevent exceeding max keywords (8)
    if (formKeywords.length >= 8) {
      setKeywordError(t("subscription.keywords.too_many"));
      return;
    }

    const trimmed = newKeyword.trim();
    const normalizedNew = trimmed.toLowerCase();
    const alreadyExists = formKeywords.some((k) => k.trim().toLowerCase() === normalizedNew);
    setNewKeyword("");
    setKeywordError(null);

    if (trimmed.length === 0 || alreadyExists) {
      return;
    }

    setFormKeywords((prevKeywords) => prevKeywords.concat([trimmed]));
    setIsDirty(true);
  };

  const removeKeyword = (label: string) => {
    setFormKeywords((prevKeywords) => prevKeywords.filter((keyword) => keyword !== label));
    setIsDirty(true);
    setKeywordError(null);
  };

  const body = (
    <>
      <CompanyLogo companyName={companyName} logoUrl={logoUrl} />
      <div className="flex flex-col gap-[4px]">
        <SectionHeader as="h1">{pageTitle}</SectionHeader>
        <Body>{t("subscription.selectJobs")}</Body>
      </div>
      <div className="subscription">
        {allowDepartmentAlerts && departments.length > 0 && (
          <div className="subscription__input">
            <MultiSelect
              values={selectedDepartments}
              label={t("subscription.department.label")}
              onChange={onSelect}
              options={departments}
              clearable
              error={departmentError}
            />
          </div>
        )}

        <div className="subscription-keyword gap-[8px]">
          <div className="subscription-keyword__input-row gap-[12px]">
            <div className="subscription-keyword__input-mobile-row gap-[12px]">
              <div className="flex flex-row items-start gap-[12px]">
                <div className="subscription-keyword__input flex-1 min-w-0">
                  <TextInput
                    id="keyword"
                    placeholder={t("subscription.keywords.placeholder")}
                    maxLength={255}
                    short={true}
                    onChange={(event) => setNewKeyword(event.currentTarget.value || "")}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        addKeyword(event);
                      }
                    }}
                    value={newKeyword}
                    narrow={true}
                    icon={<SearchIcon />}
                    error={keywordError}
                    help={t("subscription.keywords.subtitle")}
                  />
                </div>
                <HelpPopover text={t("subscription.keywords.helperText")} className="h-[40px]" />
              </div>
              <div className="subscription subscription-keyword__button">
                <SecondaryButton
                  onClick={addKeyword}
                  disabled={!!keywordError || isKeywordEmpty}
                  size="medium"
                >
                  {t("subscription.keywords.add")}
                </SecondaryButton>
              </div>
            </div>
          </div>
          <KeywordTags keywords={formKeywords} onClick={removeKeyword} />
        </div>

        {error && (
          <p className="pb-[3px] helper-text helper-text--error" aria-live="polite">
            {error}
          </p>
        )}
        <div className="subscription__buttons">
          <PrimaryButton
            onClick={() => submitSubscriptionSettings()}
            loading={isSubmitting}
            disabled={!isDirty || !(formKeywords.length || selectedDepartments.length)}
          >
            {method === "POST" ? t("subscription.create") : t("subscription.update")}
          </PrimaryButton>
          {method !== "POST" &&
            (modal ? (
              <DangerButton onClick={() => setConfirmingDelete(true)}>
                {t("subscription.delete")}
              </DangerButton>
            ) : (
              <ConfirmationModal onConfirm={handleDelete}>
                <DangerButton>{t("subscription.delete")}</DangerButton>
              </ConfirmationModal>
            ))}
          <Link href={returnPath} onClick={handleCancel}>
            {"Cancel"}
          </Link>
        </div>
      </div>
      {emailPreferencesPath && (
        <EnableAlertsModal
          open={showPausedModal}
          onOpenChange={setShowPausedModal}
          companyName={companyName}
          emailPreferencesPath={emailPreferencesPath}
          onDismiss={() => setShowPausedModal(false)}
        />
      )}
    </>
  );

  return modal ? (
    <Modal maxWidth="xl">
      <div
        key={confirmingDelete ? "confirm-delete" : "form"}
        className="subscription--modal flex flex-col gap-6 animate-in fade-in-0 duration-200"
      >
        {confirmingDelete ? (
          <DeleteConfirmation
            onConfirm={handleDelete}
            onCancel={() => setConfirmingDelete(false)}
          />
        ) : (
          body
        )}
      </div>
    </Modal>
  ) : (
    <Container>
      <BackButton href={returnPath} />
      {body}
    </Container>
  );
};

export default Subscription;
